import { Download, Loader2, Paperclip, X } from "lucide-react";
import { useRef, useState } from "react";

import { PROJECT_DOCUMENT_ACCEPT } from "@/lib/api/documents";
import type { PrebuildReviewSuggestedAnswer } from "@/lib/api/databook";
import { formatQuestionMoney } from "./questionText";

export interface QuestionAnswerAttachment {
  documentId: string;
  filename: string;
}

interface QuestionAnswerFieldProps {
  value: string;
  suggestedAnswers: PrebuildReviewSuggestedAnswer[];
  onChange: (value: string) => void;
  attachments?: QuestionAnswerAttachment[];
  uploading?: boolean;
  onAttach?: (files: File[]) => void;
  onDetach?: (documentId: string) => void;
  onDownloadAttachment?: (attachment: QuestionAnswerAttachment) => void;
  disabled?: boolean;
}

const EMPTY_ATTACHMENTS: QuestionAnswerAttachment[] = [];

export function QuestionAnswerField({
  value,
  suggestedAnswers,
  onChange,
  attachments = EMPTY_ATTACHMENTS,
  uploading = false,
  onAttach,
  onDetach,
  onDownloadAttachment,
  disabled = false,
}: QuestionAnswerFieldProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const valueIsSuggestion = suggestedAnswers.some((answer) => answer.text === value);
  const [otherChosen, setOtherChosen] = useState(false);
  const otherSelected = otherChosen
    || suggestedAnswers.length === 0
    || (value.length > 0 && !valueIsSuggestion);

  return (
    <div className="space-y-2">
      {suggestedAnswers.length > 0 && (
        <div className="grid gap-2" role="radiogroup" aria-label="Answer options">
          {suggestedAnswers.map((answer) => {
            const selected = !otherSelected && value === answer.text;
            return (
              <button
                key={answer.text}
                type="button"
                role="radio"
                disabled={disabled}
                aria-checked={selected}
                onClick={() => {
                  setOtherChosen(false);
                  onChange(answer.text);
                }}
                className={
                  "flex min-h-14 w-full items-start gap-3 rounded-lg border bg-background px-4 py-3 text-left font-medium transition-colors disabled:pointer-events-none disabled:opacity-100 "
                  + (selected
                    ? "border-foreground bg-muted/30 ring-1 ring-foreground/20"
                    : "border-border bg-background text-foreground hover:border-foreground hover:bg-muted/50")
                }
                style={{ fontSize: 11.5 }}
              >
                <span
                  aria-hidden="true"
                  className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-foreground"
                >
                  {selected && <span className="h-2 w-2 rounded-full bg-foreground" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block">{formatQuestionMoney(answer.text)}</span>
                  {answer.description && (
                    <span className="mt-1 block text-[10.5px] font-normal leading-relaxed opacity-70">
                      {formatQuestionMoney(answer.description)}
                    </span>
                  )}
                </span>
                {(answer.recommended || answer.requires_file_upload) && (
                  <span className="ml-auto mt-0.5 flex shrink-0 items-center gap-1.5">
                    {answer.recommended && (
                      <span className="rounded-full bg-muted px-2 py-0.5 text-[9.5px] font-semibold text-muted-foreground">
                        Recommended
                      </span>
                    )}
                    {answer.requires_file_upload && (
                      <span className="text-[10px] font-normal opacity-70">
                        Needs a file
                      </span>
                    )}
                  </span>
                )}
              </button>
            );
          })}
          <button
            type="button"
            role="radio"
            disabled={disabled}
            aria-checked={otherSelected}
            onClick={() => {
              setOtherChosen(true);
              if (!otherSelected) onChange("");
            }}
            className={
              "flex min-h-14 w-full items-start gap-3 rounded-lg border bg-background px-4 py-3 text-left font-medium transition-colors disabled:pointer-events-none disabled:opacity-100 "
              + (otherSelected
                ? "border-foreground bg-muted/30 ring-1 ring-foreground/20"
                : "border-border bg-background text-foreground hover:border-foreground hover:bg-muted/50")
            }
            style={{ fontSize: 11.5 }}
          >
            <span
              aria-hidden="true"
              className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-foreground"
            >
              {otherSelected && <span className="h-2 w-2 rounded-full bg-foreground" />}
            </span>
            <span>Other answer</span>
          </button>
        </div>
      )}
      {otherSelected && (
        <textarea
          autoFocus
          value={value}
          disabled={disabled}
          onChange={(event) => {
            setOtherChosen(true);
            onChange(event.target.value);
          }}
          maxLength={16000}
          rows={3}
          placeholder="Type your answer…"
          className="w-full resize-y rounded-lg border border-border bg-background px-3 py-2 outline-none focus:border-foreground disabled:pointer-events-none disabled:opacity-100"
          style={{ fontSize: 11.5 }}
        />
      )}
      <div className="space-y-2 pt-1">
        {attachments.length > 0 && (
          <div className="space-y-1.5">
            {attachments.map((attachment) => (
              <div
                key={attachment.documentId}
                className="flex min-h-10 items-center gap-2 rounded-lg border border-border bg-background px-3 py-2"
              >
                <Paperclip size={13} className="shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate font-medium" style={{ fontSize: 11.5 }}>
                  {attachment.filename}
                </span>
                {onDownloadAttachment && (
                  <button
                    type="button"
                    aria-label={`Download ${attachment.filename}`}
                    onClick={() => onDownloadAttachment(attachment)}
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    <Download size={13} />
                  </button>
                )}
                {!disabled && onDetach && (
                  <button
                    type="button"
                    aria-label={`Remove ${attachment.filename}`}
                    onClick={() => onDetach(attachment.documentId)}
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    <X size={13} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        {onAttach && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={PROJECT_DOCUMENT_ACCEPT}
              disabled={disabled || uploading}
              className="sr-only"
              aria-label="Attach files"
              onChange={(event) => {
                const files = Array.from(event.target.files ?? []);
                event.target.value = "";
                if (files.length > 0) onAttach(files);
              }}
            />
            <button
              type="button"
              disabled={disabled || uploading}
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 font-medium text-foreground/80 hover:bg-muted disabled:opacity-40"
              style={{ fontSize: 11 }}
            >
              {uploading
                ? <Loader2 size={13} className="animate-spin" />
                : <Paperclip size={13} />}
              {uploading ? "Uploading…" : "Attach files"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
